<template>
    <div>
        <card-header title="Lead" icon="fa-address-card"/>

        <entity-tabs v-model="tab" main-label="Lead" main-icon="fa-address-card" :task-count="taskCount" :task-completed-count="taskCompleted" :note-count="noteCount"/>

        <card-body>
            <entity-tasks-pane v-show="tab === 'tasks'" :card="card" :child="child" entity-type="lead" :entity-id="props.leadId" @count="taskCount = $event" @completed="taskCompleted = $event"/>
            <entity-notes-pane v-show="tab === 'notes'" :card="card" :child="child" entity-type="lead" :entity-id="props.leadId" @count="noteCount = $event"/>
            <card-list v-show="tab === 'main'">
                <subheader-list-item title="Lead Details" icon="fa-list"/>
                <data-row title="First">{{ lead.firstname }}</data-row>
                <data-row title="Last">{{ lead.lastname }}</data-row>
                <data-row title="E-Mail"><a :href="'mailto:' + lead.email" v-if="lead.email">{{ lead.email }}</a></data-row>
                <data-row title="Phone">{{ lead.phone }}</data-row>
                <data-row title="Address">
                    <div>{{ lead.address }}</div>
                    <div v-if="lead.address2">{{ lead.address2 }}</div>
                    <div>{{ lead.city }}<span v-if="lead.city">, </span>{{ lead.state }} &nbsp;{{ lead.zip }}</div>
                    <div>{{ lead.country }}</div>
                </data-row>
                <data-row title="Conversion">
                    <b-tag :type="statusType">{{ statusLabel }}</b-tag>
                </data-row>
                <data-row title="Converted" v-if="lead.converted_at">{{ lead.converted_at | moment('MMM Do, YYYY') }}</data-row>
                <data-row title="Closed" v-if="lead.lost_at">{{ lead.lost_at | moment('MMM Do, YYYY') }}</data-row>

                <subheader-list-item title="Interest" icon="fa-list"/>
                <data-row title="Source"><b-tag>{{ lead.source }}</b-tag></data-row>
                <data-row title="Tennis Club">{{ lead.club }}</data-row>
                <data-row title="Pro">{{ lead.pro }}</data-row>
                <data-row title="Interests">{{ lead.interests }}</data-row>
                <data-row title="Friends">{{ lead.friends }}</data-row>
                <data-row title="Question">{{ lead.wildcard }}</data-row>
                <data-row title="Received">{{ lead.timestamp | moment('MMM Do, YYYY, h:mm a') }}</data-row>

                <subheader-list-item title="Conversion" icon="fa-user-check"/>
                <customer-list-item
                    v-if="lead.converted_user"
                    :card="card"
                    :user="lead.converted_user"
                    :active="child && child.definition.component === 'customer' && lead.converted_user.id && child.props.userId === lead.converted_user.id.toString()"
                    @click="$openCard('customer', {userId: lead.converted_user.id}, card)"
                />
                <container-list-item padded>
                    <template v-if="lead.converted_user">
                        <b-button size="is-small" outlined :loading="saving" @click="unlinkCustomer">
                            <i class="fal fa-times mr-2"></i>Unlink customer
                        </b-button>
                    </template>
                    <template v-else>
                        <div v-if="suggestion" class="suggestion">
                            <span><i class="far fa-lightbulb mr-2"></i>Email matches customer
                                <b>{{ suggestionName }}</b></span>
                            <b-button size="is-small" type="is-primary" :loading="saving"
                                      @click="linkCustomer(suggestion.id)">Link</b-button>
                        </div>
                        <user-select :value="null" label="Link to customer" @input="linkCustomer"/>
                    </template>

                    <div class="convert-action">
                        <b-button size="is-small" outlined :loading="saving" @click="toggleConverted">
                            {{ lead.converted_at ? 'Clear converted' : 'Mark converted' }}
                        </b-button>
                        <b-button size="is-small" outlined :loading="saving" @click="toggleLost">
                            {{ lead.lost_at ? 'Clear did not convert' : 'Mark did not convert' }}
                        </b-button>
                    </div>
                </container-list-item>
            </card-list>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "../../TIER/components/CardHeader";
    import CardList from "../../TIER/components/CardList";
    import CardBody from "../../TIER/components/CardBody";
    import DataRow from "../../TIER/components/DataRow";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import UserSelect from "../../components/UserSelect";
    import CustomerListItem from "../../components/CustomerListItem";
    import {client as http} from '../../http_client';
    import EntityTabs from "@/components/EntityTabs";
    import EntityTasksPane from "@/components/EntityTasksPane";
    import EntityNotesPane from "@/components/EntityNotesPane";

    export default {
        name: "Lead",
        props: ['card', 'child', 'props'],
        components: {EntityNotesPane, EntityTasksPane, EntityTabs, DataRow, CardBody, SubheaderListItem, ContainerListItem, CardList, CardHeader, UserSelect, CustomerListItem},
        data: function() {
            return {
                tab: 'main',
                taskCount: 0,
                taskCompleted: 0,
                noteCount: 0,
                lead: {},
                suggestion: null,
                saving: false
            };
        },
        computed: {
            statusType: function() {
                return this.lead.converted_at ? 'is-success' :
                    this.lead.lost_at ? 'is-danger is-light' : 'is-light';
            },
            statusLabel: function() {
                return this.lead.converted_at ? 'Converted' :
                    this.lead.lost_at ? 'Did not convert' : 'Pending';
            },
            suggestionName: function() {
                const u = this.suggestion;
                if (!u) return '';
                return [u.last_name, u.first_name].filter(Boolean).join(', ') || u.email;
            }
        },
        methods: {
            applyLead: function(lead) {
                this.lead = lead;
                const title = [lead.firstname, lead.lastname].filter(Boolean).join(' ');
                this.$emit('title', title || lead.email);
            },
            loadLead: function(force) {
                this.$emit('loading', true);
                this.suggestion = null;
                http.get('/api/leads/' + this.props.leadId, {force}).then(response => {
                    this.applyLead(response.data);
                    this.$emit('loading', false);
                    this.loadSuggestion();
                });
            },
            // Suggest an existing customer whose email exactly matches the lead's,
            // when the lead isn't already linked (TEN-122).
            loadSuggestion: function() {
                if (this.lead.converted_user_id || !this.lead.email) return;
                const email = this.lead.email.trim();
                http.get('/api/users/search/' + encodeURIComponent(email) + '/0/5').then(response => {
                    const match = (response.data.users || []).find(
                        u => u.email && u.email.toLowerCase() === email.toLowerCase()
                    );
                    this.suggestion = match || null;
                }).catch(() => {
                    this.suggestion = null;
                });
            },
            patchLead: function(payload) {
                this.saving = true;
                return http.put('/api/leads/' + this.props.leadId, payload, {force: true}).then(response => {
                    this.applyLead(response.data);
                    this.suggestion = null;
                    this.$reloadCard('leads');
                    this.$reloadCard('search-leads');
                    // Keep the dashboard pending-leads badge in sync.
                    this.$store.dispatch('updateLeadCounts');
                }).finally(() => {
                    this.saving = false;
                });
            },
            linkCustomer: function(userId) {
                if (!userId) return;
                this.patchLead({converted_user_id: userId});
            },
            unlinkCustomer: function() {
                this.patchLead({converted_user_id: null}).then(() => this.loadSuggestion());
            },
            toggleConverted: function() {
                this.patchLead({converted: !this.lead.converted_at});
            },
            toggleLost: function() {
                this.patchLead({lost: !this.lead.lost_at});
            },
            reload: function() {
                this.loadLead(true);
            }
        },
        watch: {
            'props.leadId': function() {
                this.loadLead();
            }
        },
        mounted() {
            this.loadLead();
        }
    };
</script>

<style lang="scss" scoped>
    .convert-action {
        margin-top: 0.75rem;
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    .suggestion {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        flex-wrap: wrap;
        margin-bottom: 0.5rem;
        font-size: .85rem;
    }
</style>
